// 监管合规尽调 — Markdown 导出
// 输出格式定位：律师/合规官能直接拿走作为内部尽调文档，包含 finding + 引用 URL + 时间戳
// 纯函数，单测友好；UI 端只负责触发下载

const JURISDICTION_LABELS = {
  hk: { zh: '香港 (HK)', en: 'Hong Kong (HK)' },
  sg: { zh: '新加坡 (SG)', en: 'Singapore (SG)' },
  eu: { zh: '欧盟 (EU)', en: 'European Union (EU)' },
  us: { zh: '美国 (US)', en: 'United States (US)' },
};

const SEV_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

export function buildComplianceMarkdown({ projectName, complianceRisk, jurisdiction = 'hk', lang = 'zh' } = {}) {
  if (!projectName || !complianceRisk || !Array.isArray(complianceRisk.findings)) {
    return null;
  }

  const cr = complianceRisk;
  const jurLabel = JURISDICTION_LABELS[jurisdiction]?.[lang] || jurisdiction.toUpperCase();
  const findings = [...cr.findings].sort(
    (a, b) => (SEV_ORDER[a.severity] ?? 99) - (SEV_ORDER[b.severity] ?? 99)
  );
  const critCount = findings.filter(f => f.severity === 'critical').length;
  const highCount = findings.filter(f => f.severity === 'high').length;
  const medCount = findings.filter(f => f.severity === 'medium').length;
  const lowCount = findings.filter(f => f.severity === 'low').length;
  const topicsConsidered = cr.topics_considered?.length || 0;
  const generatedAt = cr._meta?.generated_at || new Date().toISOString();

  const lines = [];

  if (lang === 'en') {
    lines.push(`# ${projectName} — Regulatory Compliance Due Diligence`);
    lines.push('');
    lines.push(`**Jurisdiction:** ${jurLabel}  `);
    lines.push(`**Generated:** ${generatedAt.slice(0, 10)}  `);
    lines.push(`**Topics considered:** ${topicsConsidered}  `);
    lines.push(`**Overall severity:** ${(cr.overall_severity || 'n/a').toUpperCase()}`);
    lines.push('');
    lines.push('> **Scope.** This review enumerates regulatory gaps an institution operating in the above jurisdiction would need to clarify before investing in or partnering with the project. It does not represent the project\'s overall global compliance posture.');
    lines.push('');
    lines.push('## Summary');
    lines.push('');
    if (cr.summary) lines.push(cr.summary);
    lines.push('');
    lines.push('| Metric | Count |');
    lines.push('| --- | --- |');
    lines.push(`| Total findings | ${findings.length} |`);
    lines.push(`| Critical | ${critCount} |`);
    lines.push(`| High | ${highCount} |`);
    lines.push(`| Medium | ${medCount} |`);
    lines.push(`| Low | ${lowCount} |`);
    lines.push('');
    if (findings.length === 0) {
      lines.push('## Findings');
      lines.push('');
      lines.push(topicsConsidered > 0
        ? `_No critical or high gaps surfaced across ${topicsConsidered} reviewed topic(s)._`
        : '_No regulatory topics were triggered for this project._');
    } else {
      lines.push('## Findings');
      lines.push('');
      findings.forEach((f, i) => {
        lines.push(`### ${i + 1}. [${(f.severity || 'unknown').toUpperCase()}] ${f.requirement || '(unspecified requirement)'}`);
        lines.push('');
        lines.push(`**Topic:** \`${f.topic || 'n/a'}\``);
        lines.push('');
        if (f.gap) {
          lines.push(`**Gap:** ${f.gap}`);
          lines.push('');
        }
        if (f.citation?.section) {
          if (f.citation.source_url) {
            lines.push(`**Citation:** [${f.citation.section}](${f.citation.source_url})`);
          } else {
            lines.push(`**Citation:** ${f.citation.section}`);
          }
          lines.push('');
        }
      });
    }
    lines.push('');
    lines.push('---');
    lines.push('_Generated by PrimaryLens · AI-synthesized — verify against primary regulatory sources before reliance._');
  } else {
    lines.push(`# ${projectName} — 监管合规尽调报告`);
    lines.push('');
    lines.push(`**司法辖区：** ${jurLabel}  `);
    lines.push(`**生成时间：** ${generatedAt.slice(0, 10)}  `);
    lines.push(`**审视 topic 数：** ${topicsConsidered}  `);
    lines.push(`**整体严重度：** ${(cr.overall_severity || '未评级').toUpperCase()}`);
    lines.push('');
    lines.push('> **审查口径。** 本报告列举该司法辖区机构在投资或合作前需要项目方澄清的监管合规缺口，不代表项目的整体全球合规姿态。');
    lines.push('');
    lines.push('## 概况');
    lines.push('');
    if (cr.summary) lines.push(cr.summary);
    lines.push('');
    lines.push('| 指标 | 数量 |');
    lines.push('| --- | --- |');
    lines.push(`| 总缺口 | ${findings.length} |`);
    lines.push(`| Critical | ${critCount} |`);
    lines.push(`| High | ${highCount} |`);
    lines.push(`| Medium | ${medCount} |`);
    lines.push(`| Low | ${lowCount} |`);
    lines.push('');
    if (findings.length === 0) {
      lines.push('## 缺口清单');
      lines.push('');
      lines.push(topicsConsidered > 0
        ? `_已审视 ${topicsConsidered} 个 topic，未发现 critical 或 high 缺口。_`
        : '_该项目未触发任何监管 topic。_');
    } else {
      lines.push('## 缺口清单');
      lines.push('');
      findings.forEach((f, i) => {
        lines.push(`### ${i + 1}. [${(f.severity || 'unknown').toUpperCase()}] ${f.requirement || '（未指定要求）'}`);
        lines.push('');
        lines.push(`**Topic：** \`${f.topic || 'n/a'}\``);
        lines.push('');
        if (f.gap) {
          lines.push(`**缺口：** ${f.gap}`);
          lines.push('');
        }
        if (f.citation?.section) {
          if (f.citation.source_url) {
            lines.push(`**引用：** [${f.citation.section}](${f.citation.source_url})`);
          } else {
            lines.push(`**引用：** ${f.citation.section}`);
          }
          lines.push('');
        }
      });
    }
    lines.push('');
    lines.push('---');
    lines.push('_由 PrimaryLens 生成 · AI 合成产出，使用前请对照监管原文核实。_');
  }

  return lines.join('\n');
}

export function downloadComplianceMarkdown({ projectName, complianceRisk, jurisdiction = 'hk', lang = 'zh' } = {}) {
  const md = buildComplianceMarkdown({ projectName, complianceRisk, jurisdiction, lang });
  if (!md) return false;
  const safeName = (projectName || 'project').replace(/[^\w.-]+/g, '_');
  const dateStr = (complianceRisk?._meta?.generated_at || new Date().toISOString()).slice(0, 10);
  const fileName = `${safeName}-compliance-${jurisdiction}-${dateStr}.md`;
  const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return true;
}
